import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { fetchComCatBrandCatalog, type ComCatBrandItem } from '@/api/comCatBrand';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import PrevBrandPicker, {
  formatPrevBrandSelection,
  isPrevBrandSelectionValid,
} from '@/components/survey/PrevBrandPicker';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import {
  PREV_BRAND_OTHERS,
  buildOtherBrandsAnswers,
  readOtherBrandsAnswers,
  writeOtherBrandsAnswers,
} from '@/lib/otherBrandsStorage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useAppSelector } from '@/stores/hooks';
import { surveyPathAfterOtherBrands } from '@/config/codeBrand';
import {
  selectCodeBrand,
  selectPdpaConsent,
  selectSurveyScreening,
  selectUniqueCodeDraft,
} from '@/stores/selectors';

/**
 * Shared: previous smoking brand (same UX as LD `/survey/prevbrand`).
 * Continues to LBS `/7_1` or Dice `/dice/q1` based on `codeBrand`.
 */
export default function OtherBrands() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const codeBrand = useAppSelector(selectCodeBrand);
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [brands, setBrands] = useState<ComCatBrandItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState<string | null>(null);
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState(() => readOtherBrandsAnswers()?.brand ?? '');
  const [selectedVariant, setSelectedVariant] = useState(
    () => readOtherBrandsAnswers()?.variant ?? ''
  );

  useEffect(() => {
    writeOtherBrandsAnswers({ brand: selectedBrand, variant: selectedVariant });
  }, [selectedBrand, selectedVariant]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setWarning(null);
    void (async () => {
      try {
        const list = await fetchComCatBrandCatalog();
        if (cancelled) return;
        setBrands(list);
      } catch {
        if (!cancelled) setWarning(t.otherBrands.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t.otherBrands.loadError]);

  useEffect(() => {
    if (selectedBrand && selectedBrand !== PREV_BRAND_OTHERS && !expandedBrand) {
      setExpandedBrand(selectedBrand);
    }
  }, [selectedBrand, expandedBrand]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) return <Navigate to="/uniquecode" replace />;
  if (!isSmokerProfileComplete()) return <Navigate to="/smokerprofile" replace />;

  const handleSelectVariant = (brand: string, variant: string) => {
    setSelectedBrand(brand);
    setSelectedVariant(variant);
    setWarning(null);
  };

  const handleOtherSpecifyChange = (value: string) => {
    const normalized = buildOtherBrandsAnswers(PREV_BRAND_OTHERS, value);
    setSelectedBrand(normalized.brand);
    setSelectedVariant(normalized.variant);
    setExpandedBrand(null);
    setWarning(null);
  };

  const handleContinue = () => {
    if (selectedBrand === PREV_BRAND_OTHERS && !selectedVariant.trim()) {
      setWarning(t.otherBrands.otherSpecifyWarning);
      return;
    }
    if (!isPrevBrandSelectionValid(selectedBrand, selectedVariant)) {
      setWarning(t.otherBrands.requiredWarning);
      return;
    }
    writeOtherBrandsAnswers(buildOtherBrandsAnswers(selectedBrand, selectedVariant));
    navigate(surveyPathAfterOtherBrands(codeBrand));
  };

  const hasSelection = isPrevBrandSelectionValid(selectedBrand, selectedVariant);

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] min-h-0 flex-1 flex-col overflow-hidden">
        {/* pt not mt: margin on flex-1 child is clipped by overflow-hidden (cuts bottom radius) */}
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[7vh] pb-4">
          <SurveyBrownPanelInset className="shrink-0">
            <SurveyBrownPanel className="!mb-4 !p-4">
              <p className="m-0 break-words text-left text-[19px] font-bold text-white sm:text-[20px]">
                {t.otherBrands.question}
              </p>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <PrevBrandPicker
              brands={brands}
              loading={loading}
              expandedBrand={expandedBrand}
              selectedBrand={selectedBrand}
              selectedVariant={selectedVariant}
              onExpandedBrandChange={setExpandedBrand}
              onSelectVariant={handleSelectVariant}
              onOtherSpecifyChange={handleOtherSpecifyChange}
            />
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-4 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {hasSelection ? (
                <p className="m-0 text-center text-sm font-bold text-white/90">
                  {formatPrevBrandSelection(
                    selectedBrand,
                    selectedVariant,
                    t.otherBrands.otherSpecify
                  )}
                </p>
              ) : null}
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                disabled={loading}
                onClick={handleContinue}
              >
                {t.otherBrands.continue}
              </MetallicPillButton>
            </div>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
